import React, { useState, useCallback, useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useMediaQuery } from '@mui/material'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import StraightenIcon from '@mui/icons-material/Straighten'
import AssignmentIcon from '@mui/icons-material/Assignment'
import ConstructionIcon from '@mui/icons-material/Construction'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import styles from './OrderProgress.module.css'

type OrderProgressVariant = 'furniture' | 'table'

interface ProgressStep {
  labelId: string
  descId: string
  icon: React.ReactNode
}

const FURNITURE_STEPS: ProgressStep[] = [
  {
    labelId: 'orderProgress.step1',
    descId: 'orderProgress.furniture.desc1',
    icon: <ShoppingCartIcon />,
  },
  {
    labelId: 'orderProgress.step2',
    descId: 'orderProgress.furniture.desc2',
    icon: <StraightenIcon />,
  },
  {
    labelId: 'orderProgress.step3',
    descId: 'orderProgress.furniture.desc3',
    icon: <AssignmentIcon />,
  },
  {
    labelId: 'orderProgress.step4',
    descId: 'orderProgress.furniture.desc4',
    icon: <ConstructionIcon />,
  },
  {
    labelId: 'orderProgress.step5',
    descId: 'orderProgress.furniture.desc5',
    icon: <LocalShippingIcon />,
  },
]

const TABLE_STEPS: ProgressStep[] = [
  {
    labelId: 'orderProgress.table.step1',
    descId: 'orderProgress.table.desc1',
    icon: <ShoppingCartIcon />,
  },
  {
    labelId: 'orderProgress.table.step2',
    descId: 'orderProgress.table.desc2',
    icon: <AssignmentIcon />,
  },
  {
    labelId: 'orderProgress.table.step3',
    descId: 'orderProgress.table.desc3',
    icon: <ConstructionIcon />,
  },
  {
    labelId: 'orderProgress.table.step4',
    descId: 'orderProgress.table.desc4',
    icon: <LocalShippingIcon />,
  },
]

const STEPS_BY_VARIANT: Record<OrderProgressVariant, ProgressStep[]> = {
  furniture: FURNITURE_STEPS,
  table: TABLE_STEPS,
}

interface OrderProgressProps {
  activeStep?: number
  variant?: OrderProgressVariant
}

export const OrderProgress: React.FC<OrderProgressProps> = ({
  activeStep = 0,
  variant = 'furniture',
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const steps = STEPS_BY_VARIANT[variant]

  const handleStepClick = useCallback(
    (index: number) => {
      if (!isMobile) return
      setOpenIndex((prev) => (prev === index ? null : index))
    },
    [isMobile]
  )

  const handleStepEnter = useCallback(
    (index: number) => {
      if (isMobile) return
      setOpenIndex(index)
    },
    [isMobile]
  )

  const handleStepLeave = useCallback(() => {
    if (isMobile) return
    setOpenIndex(null)
  }, [isMobile])

  useEffect(() => {
    if (!isMobile) {
      setOpenIndex(null)
      return
    }
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (!target?.closest(`.${styles.step}`)) {
        setOpenIndex(null)
      }
    }
    document.addEventListener('click', handleOutside)
    return () => document.removeEventListener('click', handleOutside)
  }, [isMobile])

  return (
    <section className={styles.section}>
      <div className={styles.track}>
        <div className={styles.line} />
        {steps.map((step, index) => {
          const isOpen = openIndex === index
          const isActive =
            openIndex !== null ? isOpen : index <= activeStep
          return (
            <div
              key={step.labelId}
              className={styles.step}
              onMouseEnter={() => handleStepEnter(index)}
              onMouseLeave={handleStepLeave}
            >
              <div
                className={`${styles.tooltip} ${isOpen ? styles.tooltipOpen : ''}`}
                role="tooltip"
                aria-hidden={!isOpen}
              >
                <span className={styles.tooltipText}>
                  <FormattedMessage id={step.descId} />
                </span>
              </div>
              <button
                type="button"
                className={`${styles.circle} ${isActive ? styles.circleActive : ''}`}
                onClick={() => handleStepClick(index)}
                aria-label={`Step ${index + 1}`}
                aria-expanded={isOpen}
              >
                {step.icon}
              </button>
              <span
                className={`${styles.stepLabel} ${isActive ? styles.stepLabelActive : ''}`}
              >
                <FormattedMessage id={step.labelId} />
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}
